import React, { ReactNode } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { createPostHogConfig } from '../../services/postHogTypes';

interface PostHogProviderProps {
  children: ReactNode;
}

// Initialize PostHog once at module level
const initializePostHog = () => {
  const config = createPostHogConfig();

  // Skip initialization if PostHog is disabled for this environment
  if (config.isDisabled) {
    return null;
  }

  // Only initialize if we have an API key
  if (config.apiKey && config.apiKey !== 'phc_xxxxxxxxxxxxxxxxxxxxxx') {
    // Don't initialize in test environments
    if (typeof window === 'undefined' || import.meta.env.MODE === 'test') {
      return null;
    }

    try {
      posthog.init(config.apiKey, {
        api_host: config.host,

        // Core settings
        loaded: (_posthog) => {
          // PostHog initialized successfully
        },

        // Exception autocapture for error tracking
        capture_exceptions: true, // Enable automatic exception capture

        // Privacy and compliance
        autocapture: false, // Disable general event autocapture - use manual tracking for better control
        capture_pageview: false, // Manual page view tracking for SPAs
        capture_pageleave: true, // Track when users leave
        cross_subdomain_cookie: true,
        persistence: 'localStorage+cookie',
        person_profiles: 'identified_only', // Only create profiles for identified users (saves costs)

        // Session recording with error-based triggers via programmatic control
        session_recording: config.enableSessionRecording
          ? {
              maskAllInputs: false, // We'll manually mask sensitive inputs
              maskInputOptions: {
                password: true,
                email: false,
                tel: true,
              },
              maskTextSelector: '.ph-mask',
              blockClass: 'ph-no-capture',
              ignoreClass: 'ph-ignore',
              maskInputFn: (text: string, element?: HTMLElement) => {
                // Custom masking for sensitive data
                if (element?.getAttribute('type') === 'password') {
                  return '*'.repeat(text.length);
                }
                if (element?.getAttribute('data-private') === 'true') {
                  return '[REDACTED]';
                }
                return text;
              },
              // Note: Error-based recording is handled programmatically via
              // posthog.startSessionRecording(true) calls in error handlers
            }
          : undefined,

        // Development tools
        debug: config.environment === 'development',
        disable_session_recording:
          config.environment === 'development' && !config.enableSessionRecording,
        enable_recording_console_log: false, // Don't capture console logs (privacy)

        // Performance
        capture_performance: true,

        // Feature flags
        bootstrap: {
          distinctID: undefined,
          isIdentifiedID: false,
        },

        // Opt-out settings
        opt_out_capturing_by_default: false,
        opt_out_persistence_by_default: false,
      });

      // Set environment context
      posthog.register({
        environment_name: config.environment,
        environment_host: window.location.hostname,
        app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      });

      return posthog;
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
      return null;
    }
  } else {
    return null;
  }
};

// Initialize PostHog once when the module loads
const posthogInstance = initializePostHog();

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  // If PostHog failed to initialize, just render children
  if (!posthogInstance) {
    return <>{children}</>;
  }

  // Use the official PostHog React Provider
  return <PHProvider client={posthogInstance}>{children}</PHProvider>;
};
